import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Badge,
  TextField,
  IconButton,
  Toolbar,
  Stack,
  Divider,
  Menu,
  MenuItem
} from '@mui/material';

import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ShoppingCart,
  Inventory,
  AssignmentTurnedIn,
  LocalShipping,
  CheckCircle
} from '@mui/icons-material';

import { motion } from 'framer-motion';
import Navbar from '../navbar/navbar';
import FooterBar from '../footer/footerBar';

/* ==============================
   CONFIGURAÇÃO DE ORIGENS
============================== */
const origemConfig = {
  1: { nome: 'iFood', cor: '#ffebee', borda: '#d32f2f' },
  2: { nome: '99 Food', cor: '#fffde7', borda: '#fbc02d' },
  3: { nome: 'WebCash', cor: '#fff3e0', borda: '#ef6c00' }
};

/* ==============================
   DADOS MOCKADOS
============================== */
const mockData = {
  novos: [
    {
      codigo: '001',
      cliente: 'João Silva',
      data: '17/07/2025',
      valor: '49,90',
      telefone: '81999999999',
      endereco: 'Rua A, 123',
      formaDePagamento: 'Cartão',
      origem: 3
    }
  ],
  separacao: [
    {
      codigo: '311',
      cliente: 'Luciano',
      data: '17/07/2025',
      valor: '27,90',
      telefone: '81996417868',
      origem: 1
    },
    {
      codigo: '312',
      cliente: 'Luciano',
      data: '17/07/2025',
      valor: '27,90',
      telefone: '81996417868',
      origem: 2
    },
    {
      codigo: '313',
      cliente: 'Ana Paula',
      data: '17/07/2025',
      valor: '35,00',
      telefone: '81998887766',
      origem: 3
    }
  ],
  prontos: [
    {
      codigo: '501',
      cliente: 'Maria Lima',
      data: '17/07/2025',
      valor: '32,00',
      observacao: 'Sem cebola',
      origem: 1
    }
  ],
  transito: [
    {
      codigo: '600',
      cliente: 'Carlos Sousa',
      motoqueiro: 'Pedro',
      status: 'Saindo para entrega',
      origem: 2
    }
  ],
  entregue: [
    {
      codigo: '204',
      cliente: 'Damião',
      data: '17/07/2025',
      valor: '23,50',
      telefone: '81986666666',
      origem: 3
    }
  ]
};

/* ==============================
   LABELS DOS STATUS
============================== */
const statusLabels = [
  { key: 'novos', label: 'Novos', icon: <ShoppingCart /> },
  { key: 'separacao', label: 'Separação', icon: <Inventory /> },
  { key: 'prontos', label: 'Prontos', icon: <AssignmentTurnedIn /> },
  { key: 'transito', label: 'Deslocamento', icon: <LocalShipping /> },
  { key: 'entregue', label: 'Entregue', icon: <CheckCircle /> }
];

function PedidosDelivery() {
  const [statusAtual, setStatusAtual] = useState('novos');
  const [pesquisa, setPesquisa] = useState('');

  const [anchorEl, setAnchorEl] = useState(null);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);

  const handleTabChange = (_, newValue) => {
    setStatusAtual(newValue);
    setPesquisa('');
  };

  const handleMenuOpen = (event, pedido) => {
    setAnchorEl(event.currentTarget);
    setPedidoSelecionado(pedido);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setPedidoSelecionado(null);
  };

  const pedidos = mockData[statusAtual].filter((pedido) =>
    pedido.cliente?.toLowerCase().includes(pesquisa.toLowerCase())
  );

  return (
    <>
      <Navbar />
      <Toolbar />

      <Box sx={{ px: 4, py: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Pedidos do Delivery
        </Typography>

        {/* Ações do topo */}
        <Box display="flex" justifyContent="space-between" mb={3} gap={2}>
          <Box display="flex" gap={2}>
            <IconButton color="primary"><RefreshIcon /></IconButton>
            <IconButton color="primary"><AddIcon /></IconButton>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <SearchIcon />
            <TextField
              placeholder="Pesquisar cliente..."
              value={pesquisa}
              onChange={(e) => setPesquisa(e.target.value)}
              sx={{ minWidth: 250 }}
            />
          </Box>
        </Box>

        {/* Abas */}
        <Tabs value={statusAtual} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 3 }}>
          {statusLabels.map(({ key, label, icon }) => (
            <Tab
              key={key}
              value={key}
              label={
                <Badge badgeContent={mockData[key].length} color="secondary">
                  <Box display="flex" gap={1}>{icon} <strong>{label}</strong></Box>
                </Badge>
              }
            />
          ))}
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="h6" gutterBottom>
          {statusLabels.find((s) => s.key === statusAtual)?.label}
        </Typography>

        {/* Cards */}
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {pedidos.map((pedido, idx) => {
            const origem = origemConfig[pedido.origem];

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Box
                  sx={{
                    width: 300,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: origem.cor,
                    borderLeft: `6px solid ${origem.borda}`,
                    boxShadow: 2,
                    position: 'relative'
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ position: 'absolute', top: 8, right: 8, color: origem.borda }}
                  >
                    {origem.nome}
                  </Typography>

                  <Typography fontWeight="bold">{pedido.cliente}</Typography>
                  <Typography variant="body2">Valor: R$ {pedido.valor}</Typography>
                  {pedido.data && <Typography variant="body2">Data: {pedido.data}</Typography>}
                  {pedido.telefone && <Typography variant="body2">Telefone: {pedido.telefone}</Typography>}
                  {pedido.endereco && <Typography variant="body2">Endereço: {pedido.endereco}</Typography>}
                  {pedido.observacao && <Typography variant="body2">Obs: {pedido.observacao}</Typography>}
                  {pedido.motoqueiro && <Typography variant="body2">Motoqueiro: {pedido.motoqueiro}</Typography>}

                  {statusAtual !== 'entregue' && (
                    <Box display="flex" justifyContent="flex-end">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, pedido)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </motion.div>
            );
          })}
        </Stack>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleMenuClose}>Ação 1</MenuItem>
          <MenuItem onClick={handleMenuClose}>Ação 2</MenuItem>
        </Menu>
      </Box>

      <FooterBar />
    </>
  );
}

export default PedidosDelivery;
